
import { useEffect, useState } from 'react';

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  unlocked: boolean;
  unlockedAt?: string;
}

export const FirstTimeBadges = () => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  useEffect(() => {
    // Carregar badges do localStorage
    const savedBadges = localStorage.getItem('firstTimeBadges');
    
    if (savedBadges) {
      setBadges(JSON.parse(savedBadges));
    } else {
      // Inicializar badges
      const initialBadges: Badge[] = [
        {
          id: 'first-login',
          name: 'Primeiro Passo',
          description: 'Fez seu primeiro login',
          icon: 'ri-login-circle-line',
          color: 'bg-blue-500',
          unlocked: true,
          unlockedAt: new Date().toISOString()
        },
        {
          id: 'first-mission',
          name: 'Aventureiro Iniciante',
          description: 'Completou sua primeira missão',
          icon: 'ri-flag-line',
          color: 'bg-green-500',
          unlocked: false
        },
        {
          id: 'first-game',
          name: 'Jogador Novato',
          description: 'Jogou seu primeiro jogo',
          icon: 'ri-gamepad-line',
          color: 'bg-purple-500',
          unlocked: false
        },
        {
          id: 'first-reward',
          name: 'Primeiro Resgate',
          description: 'Resgatou sua primeira recompensa',
          icon: 'ri-gift-line',
          color: 'bg-pink-500',
          unlocked: false
        },
        {
          id: 'first-level',
          name: 'Evoluindo',
          description: 'Alcançou o nível 2',
          icon: 'ri-arrow-up-circle-line',
          color: 'bg-yellow-500',
          unlocked: false
        },
        {
          id: 'first-badge',
          name: 'Colecionador',
          description: 'Desbloqueou seu primeiro distintivo',
          icon: 'ri-medal-line',
          color: 'bg-orange-500',
          unlocked: false
        },
        {
          id: 'first-streak',
          name: 'Consistente',
          description: 'Manteve 3 dias de sequência',
          icon: 'ri-fire-line',
          color: 'bg-red-500',
          unlocked: false
        },
        {
          id: 'first-challenge',
          name: 'Desafiador',
          description: 'Completou seu primeiro desafio diário',
          icon: 'ri-trophy-line',
          color: 'bg-indigo-500',
          unlocked: false
        },
        {
          id: 'first-100-points',
          name: 'Acumulador',
          description: 'Acumulou 100 pontos',
          icon: 'ri-coin-line',
          color: 'bg-yellow-600',
          unlocked: false
        },
        {
          id: 'first-profile-complete',
          name: 'Perfil Completo',
          description: 'Completou 100% do perfil',
          icon: 'ri-user-star-line',
          color: 'bg-teal-500',
          unlocked: false
        }
      ];
      
      setBadges(initialBadges);
      localStorage.setItem('firstTimeBadges', JSON.stringify(initialBadges));
    }
  }, []);

  const unlockedCount = badges.filter(b => b.unlocked).length;
  const totalCount = badges.length;
  const progress = (unlockedCount / totalCount) * 100;

  const handleBadgeClick = (badge: Badge) => {
    setSelectedBadge(badge);
    setShowModal(true);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
              <i className="ri-medal-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Badges de Primeira Vez</h3>
              <p className="text-sm text-gray-500">Conquistas especiais</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-orange-600">{unlockedCount}/{totalCount}</div>
            <div className="text-sm text-gray-500">Desbloqueados</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Progresso Geral</span>
            <span className="font-semibold text-gray-800">{progress.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="h-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {badges.map((badge) => (
            <button
              key={badge.id}
              onClick={() => handleBadgeClick(badge)}
              className={`p-4 rounded-lg transition-all cursor-pointer ${
                badge.unlocked
                  ? 'bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-lg'
                  : 'bg-gray-100 opacity-50 hover:opacity-70'
              }`}
            >
              <div className={`w-16 h-16 mx-auto rounded-full ${badge.color} flex items-center justify-center mb-3 ${
                badge.unlocked ? 'shadow-lg' : 'grayscale'
              }`}>
                <i className={`${badge.icon} text-3xl text-white`}></i>
              </div>
              <div className="text-center">
                <div className={`font-semibold text-sm mb-1 ${
                  badge.unlocked ? 'text-gray-800' : 'text-gray-500'
                }`}>
                  {badge.name}
                </div>
                {badge.unlocked && (
                  <div className="flex items-center justify-center gap-1 text-xs text-green-600">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>Desbloqueado</span>
                  </div>
                )}
                {!badge.unlocked && (
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
                    <i className="ri-lock-line"></i>
                    <span>Bloqueado</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Motivational Message */}
        {unlockedCount < totalCount && (
          <div className="mt-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4 text-center">
            <div className="text-orange-600 font-semibold">
              🎯 Continue explorando para desbloquear mais {totalCount - unlockedCount} badges!
            </div>
          </div>
        )}

        {unlockedCount === totalCount && (
          <div className="mt-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-4 text-center text-white">
            <i className="ri-trophy-fill text-4xl mb-2"></i>
            <div className="font-bold text-lg">🎉 Coleção Completa! 🎉</div>
            <div className="text-sm opacity-90 mt-1">Você desbloqueou todos os badges de primeira vez!</div>
          </div>
        )}
      </div>

      {/* Badge Detail Modal */}
      {showModal && selectedBadge && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 animate-scale-in">
            <div className="text-center">
              <div className={`w-24 h-24 mx-auto rounded-full ${selectedBadge.color} flex items-center justify-center mb-4 shadow-xl ${
                !selectedBadge.unlocked && 'grayscale'
              }`}>
                <i className={`${selectedBadge.icon} text-5xl text-white`}></i>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {selectedBadge.name}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {selectedBadge.description}
              </p>

              {selectedBadge.unlocked ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2 text-green-600 font-semibold mb-2">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    <span>Badge Desbloqueado!</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Desbloqueado em {new Date(selectedBadge.unlockedAt!).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2 text-gray-500 font-semibold mb-2">
                    <i className="ri-lock-line text-xl"></i>
                    <span>Badge Bloqueado</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Complete a ação para desbloquear este badge
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowModal(false)}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
